import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Switch from "react-switch";
import Swal from "sweetalert2";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import RulesFeatureTour, {
  markRulesTourSeen,
  shouldAutoStartRulesTour,
} from "./RulesFeatureTour";

const LANGUAGES = [
  { value: "english", label: "English" },
  { value: "hindi", label: "Hindi" },
  { value: "marathi", label: "Marathi" },
];

const MAX_RULES = 6;
const MIN_RULES = 1;
const MAX_RULE_CHARS = 200;
const MIN_POINTS = 1;
const MAX_POINTS = 100;

const NAMED_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

function isThemeFlagOn(val, defaultOn = false) {
  if (val == null || val === "") return defaultOn;
  return String(val).toLowerCase() === "true";
}

function toColorInputValue(val) {
  if (!val || typeof val !== "string") return "#000000";
  const v = val.trim();
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_COLORS[v.toLowerCase()];
  if (named) return named;
  return "#000000";
}

const clampInt = (n, min, max, fallback) => {
  const x = parseInt(String(n), 10);
  if (Number.isNaN(x)) return fallback;
  return Math.min(max, Math.max(min, x));
};

const normalizeMarksInput = (raw, max) => {
  const digits = String(raw).replace(/\D/g, "");
  if (digits === "") return "";
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1) return "";
  if (n > max) return String(max);
  return String(n);
};

function parseLanguagePack(raw) {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
  if (typeof raw !== "string") return {};
  const trimmed = raw.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) return parsed;
  } catch {
    /* not JSON */
  }
  if (!/^a:\d+:\{/.test(trimmed)) return {};
  const pack = {};
  const rulesMatch = trimmed.match(/s:5:"rules";a:\d+:\{([^}]*)\}/);
  if (rulesMatch) {
    pack.rules = [];
    const itemRe = /s:\d+:"((?:\\.|[^"\\])*)"/g;
    let m;
    while ((m = itemRe.exec(rulesMatch[1]))) {
      pack.rules.push(m[1].replace(/\\"/g, '"'));
    }
  }
  const pairRe = /s:\d+:"((?:\\.|[^"\\])*)";s:\d+:"((?:\\.|[^"\\])*)"/g;
  let m;
  while ((m = pairRe.exec(trimmed))) {
    if (m[1] !== "rules") pack[m[1]] = m[2].replace(/\\"/g, '"');
  }
  return pack;
}

function parseRulesList(raw) {
  if (Array.isArray(raw)) return raw.map((r) => String(r ?? ""));
  if (typeof raw === "string" && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map((r) => String(r ?? ""));
    } catch {
      /* not JSON */
    }
    if (raw.startsWith("a:")) {
      const items = [];
      const re = /s:\d+:"((?:\\.|[^"\\])*)"/g;
      let m;
      while ((m = re.exec(raw))) items.push(m[1].replace(/\\"/g, '"'));
      if (items.length) return items;
    }
  }
  return [];
}

function rulesForLanguage(packs, language, fallbackRules) {
  const fromLang = parseRulesList(packs?.[language]?.rules);
  if (fromLang.length) return fromLang;
  if (language === "english") {
    const fromFallback = parseRulesList(fallbackRules);
    if (fromFallback.length) return fromFallback;
  }
  return [""];
}

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [loading, setLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState(true);
  const [multiLanguage, setMultiLanguage] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("english");
  const [languagePacks, setLanguagePacks] = useState({
    english: {},
    hindi: {},
    marathi: {},
  });
  const [rules, setRules] = useState([""]);
  const [points, setPoints] = useState(String(MIN_POINTS));
  const [titleColor, setTitleColor] = useState("#000000");
  const [buttonColor, setButtonColor] = useState("#e9695e");
  const [buttonTextColor, setButtonTextColor] = useState("#ffffff");
  const [questionBoxColor, setQuestionBoxColor] = useState("#fbf99f");
  const [boardTextColor, setBoardTextColor] = useState("#000000");
  const [tourOpen, setTourOpen] = useState(false);

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (status === "loading" && !data) return undefined;
    if (!shouldAutoStartRulesTour()) return undefined;
    const t = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(t);
  }, [status, data]);

  const closeTour = () => {
    markRulesTourSeen();
    setTourOpen(false);
  };

  useEffect(() => {
    if (!data) return;
    const packs = {
      english: parseLanguagePack(data.english),
      hindi: parseLanguagePack(data.hindi),
      marathi: parseLanguagePack(data.marathi),
    };
    setLanguagePacks(packs);
    setLeaderboard(isThemeFlagOn(data.leaderboard, true));
    setMultiLanguage(isThemeFlagOn(data.MultiLanguage));
    setSelectedLanguage("english");
    setRules(rulesForLanguage(packs, "english", data.rules));
    setPoints(String(clampInt(data.points, MIN_POINTS, MAX_POINTS, MIN_POINTS)));
    setTitleColor(toColorInputValue(data.landing_page_title_color));
    setButtonColor(toColorInputValue(data.button_color || "#e9695e"));
    setButtonTextColor(toColorInputValue(data.button_Textcolor ?? data.button_text_color));
    setQuestionBoxColor(toColorInputValue(data.questionBox_color || "#fbf99f"));
    setBoardTextColor(toColorInputValue(data.board_Textcolor || "#000000"));
  }, [data]);

  const persistFlag = useCallback(
    async (key, on) => {
      if (!adminToken || !currentTheme) return false;
      try {
        await updateThemeData({
          payload: {
            data: { [key]: on ? "true" : "false" },
            organizationId: organizationId || "admin",
            sessionId: sessionId || "admin",
            currentTheme,
          },
          token: adminToken,
        });
        return true;
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to update setting.", "error");
        return false;
      }
    },
    [adminToken, currentTheme, organizationId, sessionId]
  );

  const handleLeaderboardChange = async (on) => {
    const prev = leaderboard;
    setLeaderboard(on);
    const ok = await persistFlag("leaderboard", on);
    if (!ok) setLeaderboard(prev);
  };

  const handleMultiLanguageChange = async (on) => {
    const prev = multiLanguage;
    setMultiLanguage(on);
    const updatedPacks = {
      ...languagePacks,
      [selectedLanguage || "english"]: {
        ...(languagePacks[selectedLanguage || "english"] || {}),
        rules,
      },
    };
    setLanguagePacks(updatedPacks);
    if (!on) {
      setSelectedLanguage("english");
      const englishRules = rulesForLanguage(updatedPacks, "english", data?.rules);
      setRules(englishRules.length ? englishRules : [""]);
    } else if (!selectedLanguage) {
      setSelectedLanguage("english");
    }
    const ok = await persistFlag("MultiLanguage", on);
    if (!ok) setMultiLanguage(prev);
  };

  const handleLanguageChange = (nextLang) => {
    if (!nextLang) {
      setLanguagePacks((packs) => ({
        ...packs,
        [selectedLanguage || "english"]: { ...(packs[selectedLanguage || "english"] || {}), rules },
      }));
      setSelectedLanguage("");
      return;
    }
    if (nextLang === selectedLanguage) return;
    const updatedPacks = {
      ...languagePacks,
      [selectedLanguage || "english"]: { ...(languagePacks[selectedLanguage || "english"] || {}), rules },
    };
    setLanguagePacks(updatedPacks);
    setSelectedLanguage(nextLang);
    const loaded = rulesForLanguage(updatedPacks, nextLang, data?.rules);
    setRules(loaded.length ? loaded : [""]);
  };

  const updateRule = (index, value) => {
    setRules((prev) => prev.map((rule, i) => (i === index ? value : rule)));
  };

  const addRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Error", `Maximum ${MAX_RULES} rules allowed`, "error");
      return;
    }
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    if (rules.length <= MIN_RULES) {
      Swal.fire("Error", "At least 1 rule is required", "error");
      return;
    }
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const trimmed = rules.map((r) => String(r ?? "").trim());
    const filled = trimmed.filter(Boolean);
    const errors = [];

    if (multiLanguage && !selectedLanguage) {
      errors.push("Please select a language");
    }
    if (!filled.length) {
      errors.push("At least 1 rule is required");
    }
    const longRules = trimmed
      .map((text, i) => (text.length > MAX_RULE_CHARS ? i + 1 : null))
      .filter(Boolean);
    if (longRules.length) {
      errors.push(`Rules ${longRules.join(", ")} exceed ${MAX_RULE_CHARS} characters`);
    }
    if (rules.length > MAX_RULES) {
      errors.push(`Maximum ${MAX_RULES} rules allowed`);
    }

    if (errors.length) return { ok: false, msg: errors.join("\n") };

    const langKey = multiLanguage ? selectedLanguage : "english";
    const existingPack =
      languagePacks[langKey] && typeof languagePacks[langKey] === "object"
        ? languagePacks[langKey]
        : {};

    const payload = {
      landing_page_title_color: titleColor,
      button_color: buttonColor,
      button_Textcolor: buttonTextColor,
      questionBox_color: questionBoxColor,
      board_Textcolor: boardTextColor,
      points: clampInt(points, MIN_POINTS, MAX_POINTS, MIN_POINTS),
      leaderboard: leaderboard ? "true" : "false",
      MultiLanguage: multiLanguage ? "true" : "false",
      [langKey]: { ...existingPack, rules: filled },
    };
    if (langKey === "english") payload.rules = filled;

    return { ok: true, payload, langKey, filled };
  };

  const handleSave = async ({ goNext = false } = {}) => {
    const v = validate();
    if (!v.ok) {
      Swal.fire("Error", v.msg, "error");
      return;
    }
    if (!adminToken || !currentTheme) {
      Swal.fire("Error", "Admin session or theme is missing.", "error");
      return;
    }

    setLoading(true);
    try {
      await updateThemeData({
        payload: {
          data: v.payload,
          organizationId: organizationId || "admin",
          sessionId: sessionId || "admin",
          currentTheme,
        },
        token: adminToken,
      });

      setLanguagePacks((prev) => ({
        ...prev,
        [v.langKey]: { ...(prev[v.langKey] || {}), rules: v.filled },
      }));

      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire("Success", "Data Updated", "success");
      if (goNext) navigate("/admin/themeupdate");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setLoading(false);
    }
  };

  const switchProps = useMemo(
    () => ({
      onColor: buttonColor || "#e25569",
      offColor: "#cbd5e1",
      onHandleColor: "#ffffff",
      offHandleColor: "#ffffff",
      height: 22,
      width: 48,
      handleDiameter: 18,
      uncheckedIcon: false,
      checkedIcon: false,
    }),
    [buttonColor]
  );

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder rules-top-bar admin-tour-top-bar">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
        <button
          type="button"
          className="admin-tour-launch"
          onClick={() => setTourOpen(true)}
          aria-label="Start feature tour"
        >
          <i className="fa-solid fa-route" aria-hidden />
          Take a tour
        </button>
      </div>

      <div className="themeupdate-row rules-php-layout rules-php-layout--two">
        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card" data-tour="tour-theme-settings">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Theme settings</h4>

              <div className="rules-tour-toggles" data-tour="tour-toggles">
                <div className="rules-php-marks-row rules-php-toggle-row">
                  <label htmlFor="rules-leaderboard" className="rules-php-marks-label">
                    Enable leaderboard
                  </label>
                  <Switch
                    id="rules-leaderboard"
                    checked={leaderboard}
                    onChange={handleLeaderboardChange}
                    disabled={loading}
                    {...switchProps}
                  />
                </div>

                <div className="rules-php-marks-row rules-php-toggle-row">
                  <label htmlFor="rules-multi-language" className="rules-php-marks-label">
                    Enable Language Selection
                  </label>
                  <Switch
                    id="rules-multi-language"
                    checked={multiLanguage}
                    onChange={handleMultiLanguageChange}
                    disabled={loading}
                    {...switchProps}
                  />
                </div>

                {multiLanguage ? (
                  <div className="rules-php-language-block">
                    <h4 className="themeupdate-label rules-php-card-title">Select the language</h4>
                    <select
                      id="languageSelect"
                      className="themeupdate-input rules-php-select"
                      value={selectedLanguage}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                    >
                      <option value="">Select language</option>
                      {LANGUAGES.map((lang) => (
                        <option key={lang.value} value={lang.value}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>

              <div className="rules-tour-custom-rules" data-tour="tour-custom-rules">
                <h4 className="themeupdate-label rules-php-card-title">Add Custom Rules</h4>
                <div className="rules-php-repeater">
                  {rules.map((rule, index) => {
                    const charCount = String(rule ?? "").length;
                    const overLimit = charCount > MAX_RULE_CHARS;
                    return (
                      <div className="rules-php-marks-row rules-php-repeater-row" key={`rule-${index}`}>
                        <div className="rules-php-repeater-field">
                          <textarea
                            name="rules"
                            className="themeupdate-input rules-php-repeater-textarea"
                            placeholder={`Enter rule (max ${MAX_RULE_CHARS} characters)`}
                            value={rule}
                            onChange={(e) => updateRule(index, e.target.value)}
                          />
                          <p className={`rules-php-char-count${overLimit ? " is-over" : ""}`}>
                            {charCount}/{MAX_RULE_CHARS}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="themeupdate-btn themeupdate-btn-danger"
                          onClick={() => removeRule(index)}
                          disabled={rules.length <= MIN_RULES || loading}
                          aria-label="Remove rule"
                        >
                          <i className="fa-solid fa-xmark" aria-hidden />
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={addRule}
                  disabled={rules.length >= MAX_RULES || loading}
                >
                  <i className="fa-solid fa-plus" aria-hidden /> Add new rule
                </button>
                <p className="themeupdate-muted rules-php-hints">
                  <code>* Keep {MAX_RULES} rules at the most</code>
                  <br />
                  <code>* At least {MIN_RULES} rule is required</code>
                  <br />
                  <code>* Character limit of each rule is {MAX_RULE_CHARS}</code>
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card" data-tour="tour-page-content">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Page Content</h4>

              <div className="rules-php-marks-row">
                <label htmlFor="rules-points" className="rules-php-marks-label">
                  Total Marks Per Right Answer
                </label>
                <input
                  id="rules-points"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="themeupdate-input rules-php-number"
                  value={points}
                  onChange={(e) => setPoints(normalizeMarksInput(e.target.value, MAX_POINTS))}
                  onBlur={() => {
                    const n = parseInt(points, 10);
                    if (points === "" || Number.isNaN(n) || n < MIN_POINTS) {
                      setPoints(String(MIN_POINTS));
                    } else {
                      setPoints(String(clampInt(n, MIN_POINTS, MAX_POINTS, MIN_POINTS)));
                    }
                  }}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-title-color" className="rules-php-marks-label">
                  Text Color
                </label>
                <input
                  id="rules-title-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={titleColor}
                  onChange={(e) => setTitleColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-btn-color" className="rules-php-marks-label">
                  Button Color
                </label>
                <input
                  id="rules-btn-color"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={buttonColor}
                  onChange={(e) => setButtonColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-btn-text" className="rules-php-marks-label">
                  Button Text Color
                </label>
                <input
                  id="rules-btn-text"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={buttonTextColor}
                  onChange={(e) => setButtonTextColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-question-box" className="rules-php-marks-label">
                  Question Box Color
                </label>
                <input
                  id="rules-question-box"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={questionBoxColor}
                  onChange={(e) => setQuestionBoxColor(e.target.value)}
                />
              </div>

              <div className="rules-php-marks-row rules-php-color-row">
                <label htmlFor="rules-board-text" className="rules-php-marks-label">
                  Board Text Color
                </label>
                <input
                  id="rules-board-text"
                  type="color"
                  className="themeupdate-color-input rules-php-color-input"
                  value={boardTextColor}
                  onChange={(e) => setBoardTextColor(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rules-action-holder" data-tour="tour-actions">
        <button
          type="button"
          className="save-and-continue"
          onClick={() => handleSave({ goNext: true })}
          disabled={loading}
        >
          {loading ? "Saving..." : "Next"} <i className="fa-solid fa-angle-double-right" aria-hidden />
        </button>
      </div>

      <RulesFeatureTour open={tourOpen} onClose={closeTour} />
    </>
  );
};

export default Rules;
